import React from "react";
import dayjs from "dayjs";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../../components/header-components/AdministrationHeader.jsx";
import { HeaderPortal } from "../../../components/ui-components/HeaderSlot";
import FreshnessLabel from "../../../components/ui-components/FreshnessLabel";
import { useOwnershipAccountability } from "./useOwnershipAccountability";
import OwnershipVerdict from "./OwnershipVerdict";
import OwnershipKpis from "./OwnershipKpis";
import OwnershipCompositionBar from "./OwnershipCompositionBar";
import OwnerHierarchyTable from "./OwnerHierarchyTable";
import { statusTone, unassignedSeverity, worstTone, pct } from "./ownershipFormat";

// Coverage is a current-state question, measured over a rolling trailing window (matches the
// Financial Control Unassigned tile + the allocation page), so the figures never start near zero
// on the 1st and climb all month.
const WINDOW_LABEL = "last 30 days";

// OwnershipAccountabilityPage answers one executive question: "Who owns this spend — and is
// anything unowned?" Verdict → KPI tiles → owner table (the actual owners, ranked by spend).
// Single organisational lens — accountability is organizational. Sibling to Financial Control.
const OwnershipAccountabilityPage = () => {
  const { data, isLoading, isError, isFetching, refetch } = useOwnershipAccountability();
  // Freshness stamp: when the figures were computed (end of the coverage window). Falls back to
  // today if the backend didn't stamp it, so the stamp always shows once data has loaded.
  const asOfLabel = data ? (data.asOf ? dayjs(data.asOf) : dayjs()).format("D MMM YYYY") : null;

  let content;
  if (isLoading) {
    content = (
      <div data-testid="oa-loading">
        <div className="h-20 animate-pulse rounded-card bg-surface-2" />
        <div className="mt-3 h-14 animate-pulse rounded-card bg-surface-2" />
        <div className="mt-3 space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 animate-pulse rounded-ctrl bg-surface-2" />
          ))}
        </div>
      </div>
    );
  } else if (isError || !data) {
    content = (
      <div className="flex items-center justify-between gap-4 rounded-card border border-danger bg-danger-soft px-5 py-4 text-sm text-danger">
        <span>We couldn&apos;t load the ownership status right now. Please try again shortly.</span>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="whitespace-nowrap rounded-ctrl border border-danger bg-card px-3 py-1.5 text-xs font-medium text-danger hover:bg-danger-soft disabled:opacity-60"
        >
          {isFetching ? "Retrying…" : "Retry"}
        </button>
      </div>
    );
  } else {
    const owners = data.owners || [];
    const ownedCost = Number(data.totalOwnedCost) || 0;
    const unassigned = data.unassigned || {};
    const unassignedCost = Number(unassigned.cost) || 0;
    const grandTotal = ownedCost + unassignedCost;
    const unassignedPct = unassigned.pct != null ? Number(unassigned.pct) : grandTotal > 0 ? unassignedCost / grandTotal : 0;
    const ownedPct = grandTotal > 0 ? ownedCost / grandTotal : 0;
    const ownershipUnconfigured = owners.length === 0 && unassignedCost === 0;
    // Single severity source: the banner takes the worse of the backend verdict and the
    // unowned severity, so it can never read amber while the Unowned tile reads red.
    const bannerTone = worstTone(statusTone(data.overall && data.overall.status), unassignedSeverity(unassignedCost, unassignedPct));
    // Balance the negative headline with the reassurance the data supports — but only when there
    // is unowned spend (otherwise the headline already says 100% owned).
    const reassurance = Math.round(unassignedCost) >= 1 ? `${pct(ownedPct)} clearly owned` : undefined;
    // Reserve the full-red wash for a genuinely severe gap; a moderate (or improving) gap reads
    // calm. Owners are spend-ranked by the backend, so owners[0] is the largest.
    const severe = bannerTone === "critical" && unassignedPct >= 0.25;
    const topOwner = owners.length && grandTotal > 0 ? { name: owners[0].ownerName, share: (Number(owners[0].cost) || 0) / grandTotal } : null;
    // Coverage trend (owned % per day) for the Owned tile's sparkline — from the nightly snapshot.
    const coverageSpark = Array.isArray(data.coverageTrend) ? data.coverageTrend.map((p) => Number(p.ownedPct) || 0) : [];
    content = (
      <>
        <OwnershipVerdict overall={data.overall} tone={bannerTone} reassurance={reassurance} severe={severe} />
        {ownershipUnconfigured ? (
          <div data-testid="oa-setup-guidance" className="rounded-card border border-card-bd bg-card px-6 py-8 text-center text-sm text-text-3">
            Define your Organisation hierarchy and owner rules to see accountability here.
          </div>
        ) : (
          <>
            <OwnershipKpis
              total={grandTotal}
              ownedCost={ownedCost}
              unowned={unassigned}
              unassignedPct={unassignedPct}
              ownerCount={owners.length}
              topOwner={topOwner}
              coverageSpark={coverageSpark}
            />
            <OwnershipCompositionBar owners={owners} grandTotal={grandTotal} unassignedCost={unassignedCost} />
            <OwnerHierarchyTable owners={owners} grandTotal={grandTotal} unassignedCost={unassignedCost} />
          </>
        )}
      </>
    );
  }

  return (
    <>
      <AdministrationHeader
        title="Economic Control"
        className="text-text-3"
        icon={icon({ name: "display-chart-up", style: "light" })}
        filters={[]}
        showPeriodSelector={false}
      />
      {/* width:100% is inline because this project's tailwind.config overrides theme.width with a
          custom scale (64/100/128/164) that drops `w-full`, so the utility is a no-op here. Without
          an explicit width the container is a shrink-to-fit flex item (mx-auto's auto margins defeat
          the parent's align-items:stretch), so it floated narrow + centered. Filling to the same
          max-w-screen-2xl cap as Financial Control keeps the sibling dashboards visually consistent. */}
      {/* Window + as-of stamp live in the top bar (portal), matching Financial Control; no separate
          section headline above the verdict (FC has none either). */}
      <HeaderPortal slot="row2-right">
        <div className="flex items-center gap-1.5 whitespace-nowrap" title="Coverage is measured over a rolling trailing window (matches Financial Control and the allocation page).">
          <span className="text-[11px] text-text-3">{WINDOW_LABEL}</span>
          {asOfLabel && (
            <>
              <span aria-hidden className="text-[#C6CDD8] select-none">·</span>
              <FreshnessLabel>as of {asOfLabel}</FreshnessLabel>
            </>
          )}
        </div>
      </HeaderPortal>
      <div className="mx-auto max-w-screen-2xl space-y-3 p-6" style={{ width: "100%" }}>
        {content}
      </div>
    </>
  );
};

export default OwnershipAccountabilityPage;
